import { useState } from 'react';
import { Pressable, View } from 'react-native';
import { router, useLocalSearchParams } from 'expo-router';
import { Languages } from 'lucide-react-native';
import { colors, crewLabels, daysFor, familyOfIcon, scheduleFor, MAX_TEXT_LENGTH, pick, searchTaskLibrary, translationPending, type CatalogTask, type LocalText, type Weekday, type WorkerLang } from '@hogar/shared';
import { Button, Card, ErrorText, Field, Label, Screen, Txt, confirm, useT } from '../../components/ui';
import { BackHeader } from '../../components/BackHeader';
import { DaysPicker, Stepper } from '../../components/forms';
import { api } from '../../lib/api';
import { useAction } from '../../lib/session';
import { useHouse } from '../../lib/house';

/** Add a task to a room, or edit one: name, minutes, days, and written instructions. */
export default function TaskEditor() {
  const { id, roomId } = useLocalSearchParams<{ id: string; roomId?: string }>();
  const { t, lang } = useT();
  const { tasks, rooms, household, staff } = useHouse();
  const act = useAction();
  const task = id === 'new' ? undefined : tasks.find((tk) => tk.id === id);
  const [areaId, setAreaId] = useState<string | undefined>(task?.roomId ?? roomId);
  const room = rooms.find((r) => r.id === areaId);
  const [name, setName] = useState(task ? pick(task.name, lang) : '');
  const [minutes, setMinutes] = useState(task?.minutes ?? 10);
  // A new task starts on its area's crew days (the garden's are its own).
  const crewDays = scheduleFor(household, room?.crew ?? 'house').workDays;
  const [days, setDays] = useState<Weekday[]>(task?.days ?? crewDays);
  const [instructions, setInstructions] = useState(task?.instructions ? pick(task.instructions, lang) : '');
  // English-speaking owners can see and correct the Spanish that staff read.
  const [nameEs, setNameEs] = useState(task?.name.src === 'en' ? task.name.es : '');
  const [instructionsEs, setInstructionsEs] = useState(task?.instructions?.src === 'en' ? task.instructions.es : '');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const back = () => (router.canGoBack() ? router.back() : router.replace('/rutinas'));

  const save = async () => {
    setBusy(true);
    try {
      const fields = { name: name.trim(), minutes, days, instructions: instructions.trim() };
      if (task) {
        // Only send the Spanish when the owner changed it by hand.
        const es: { nameEs?: string; instructionsEs?: string } = {};
        if (task.name.src === 'en' && nameEs.trim() && nameEs.trim() !== task.name.es) es.nameEs = nameEs.trim();
        if (task.instructions?.src === 'en' && instructionsEs.trim() && instructionsEs.trim() !== task.instructions.es) es.instructionsEs = instructionsEs.trim();
        // Moving to another area: the server fits the days when it crosses between house and garden.
        const moved = areaId && areaId !== task.roomId;
        await act(() => api('PATCH', `/tasks/${task.id}`, moved ? { ...fields, days: undefined, roomId: areaId, ...es } : { ...fields, ...es }));
      }
      else await act(() => api('POST', '/tasks', { ...fields, roomId: room?.id, instructions: fields.instructions || undefined }));
      back();
    } catch {
      setError(t('errGeneric'));
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!task || !(await confirm(t('confirmDeleteTask'), t('delete'), t('cancel')))) return;
    await act(() => api('DELETE', `/tasks/${task.id}`));
    back();
  };

  return (
    <Screen>
      <BackHeader title={task ? pick(task.name, lang) : t('newTask')} subtitle={room ? pick(room.name, lang) : undefined} fallback="/rutinas" />

      <Label>{t('taskName')}</Label>
      <Field value={name} onChange={setName} autoFocus={!task} />
      {!task && (
        <TaskIdeas
          query={name}
          family={room ? familyOfIcon(room.icon) : undefined}
          have={tasks.filter((tk) => tk.roomId === room?.id).map((tk) => tk.name.en.toLowerCase())}
          onPick={(idea) => {
            setName(idea.name[lang]);
            setMinutes(idea.minutes);
            setDays(daysFor(idea.frequency, crewDays));
          }}
        />
      )}

      {task && rooms.length > 1 && (
        <>
          <Label>{t('areaLabel')}</Label>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
            {rooms.map((r) => {
              const on = r.id === areaId;
              return (
                <Pressable key={r.id} onPress={() => setAreaId(r.id)} style={{ paddingVertical: 8, paddingHorizontal: 12, borderRadius: 18, borderWidth: 1.5, borderColor: on ? colors.primary : colors.cardBorder, backgroundColor: on ? colors.primarySoft : colors.card }}>
                  <Txt size={14} weight={on ? 'semibold' : 'regular'} color={on ? colors.primary : colors.text}>
                    {pick(r.name, lang)}{r.crew === 'garden' ? ` · ${crewLabels.garden[lang]}` : ''}
                  </Txt>
                </Pressable>
              );
            })}
          </View>
        </>
      )}

      <Label>{t('minutes')}</Label>
      <Card style={{ alignItems: 'center', paddingVertical: 10 }}>
        <Stepper value={minutes} min={5} max={240} step={5} format={(n) => `${n} min`} onChange={setMinutes} />
      </Card>

      <Label>{t('days')}</Label>
      <DaysPicker value={days} onChange={setDays} lang={lang} />
      {days.length === 0 && (
        <Txt size={13} color={colors.warningText} style={{ marginTop: 6 }}>
          {t('noDaysWarning')}
        </Txt>
      )}

      <Label>{t('instructions')}</Label>
      <Field value={instructions} onChange={setInstructions} multiline maxLength={MAX_TEXT_LENGTH} placeholder={t('noInstructions')} />
      <Txt size={12} color={colors.textMuted} style={{ textAlign: 'right', marginTop: 4 }}>
        {instructions.length}/{MAX_TEXT_LENGTH}
      </Txt>

      {task && <StaffPreview name={task.name} instructions={task.instructions} workerLang={staff.find((s) => s.crew === (room?.crew ?? 'house'))?.lang ?? 'es'} />}

      {task && lang === 'en' && (task.name.src === 'en' || task.instructions?.src === 'en') && (
        <>
          <Label>{t('spanishForStaff')}</Label>
          {task.name.src === 'en' && (
            <>
              <Txt size={13} color={colors.textMuted} style={{ marginBottom: 4 }}>
                {t('taskNameEs')}
              </Txt>
              <Field value={nameEs} onChange={setNameEs} />
            </>
          )}
          {task.instructions?.src === 'en' && (
            <>
              <Txt size={13} color={colors.textMuted} style={{ marginTop: 10, marginBottom: 4 }}>
                {t('instructionsEs')}
              </Txt>
              <Field value={instructionsEs} onChange={setInstructionsEs} multiline maxLength={MAX_TEXT_LENGTH} />
            </>
          )}
          {(task.name.ownerEdited || task.instructions?.ownerEdited) && (
            <Txt size={12} color={colors.textMuted} style={{ marginTop: 6 }}>
              {t('correctedByYou')}
            </Txt>
          )}
        </>
      )}

      <ErrorText>{error}</ErrorText>
      <Button label={t('save')} onPress={save} disabled={busy || !name.trim() || !room} style={{ marginTop: 14 }} />
      {task && <Button label={t('deleteTask')} variant="danger" onPress={remove} style={{ marginTop: 10 }} />}
    </Screen>
  );
}

/** What the staff link shows for this task, in Spanish. */
/** Task ideas for this kind of room, narrowed as the owner types; picking one fills in name, minutes, and days. */
function TaskIdeas({ query, family, have, onPick }: { query: string; family: ReturnType<typeof familyOfIcon>; have: string[]; onPick: (idea: CatalogTask) => void }) {
  const { t, lang } = useT();
  // Leaves out what this room already has.
  const ideas = searchTaskLibrary(query, lang, family, 60).filter((idea) => !have.includes(idea.name.en.toLowerCase())).slice(0, 6);
  if (ideas.length === 0) return null;
  return (
    <View style={{ marginTop: 8 }}>
      <Txt size={13} color={colors.textMuted} style={{ marginBottom: 6 }}>
        {t('taskIdeas')}
      </Txt>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
        {ideas.map((idea) => (
          <Pressable
            key={idea.name.en}
            onPress={() => onPick(idea)}
            style={{ paddingVertical: 8, paddingHorizontal: 12, borderRadius: 18, backgroundColor: colors.primarySoft }}
          >
            <Txt size={14} color={colors.primary}>
              {idea.name[lang]}
            </Txt>
          </Pressable>
        ))}
      </View>
    </View>
  );
}

/** What the area's worker sees, in their language. */
function StaffPreview({ name, instructions, workerLang }: { name: LocalText; instructions: LocalText | null; workerLang: WorkerLang }) {
  const { t, lang } = useT();
  const overLimit = !!(name.overLimit || instructions?.overLimit);
  const waiting = !overLimit && (!!name.pending || !!instructions?.pending) && (translationPending(name, workerLang) || (instructions ? translationPending(instructions, workerLang) : false));
  const englishWaiting = lang === 'en' && (translationPending(name, 'en') || (instructions ? translationPending(instructions, 'en') : false));
  return (
    <Card style={{ marginTop: 10, paddingVertical: 14, backgroundColor: colors.primarySoft, borderColor: colors.primarySoft }}>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <Languages size={16} color={colors.primaryDark} />
        <Txt size={13} weight="semibold" color={colors.primaryDark}>
          {lang === 'en' ? t('staffSees') : t('staffSeesSame')}
        </Txt>
      </View>
      <Txt weight="medium">{pick(name, workerLang, 'usted')}</Txt>
      {instructions ? <Txt>{pick(instructions, workerLang, 'usted')}</Txt> : null}
      {waiting ? (
        <Txt size={13} color={colors.textMuted} style={{ marginTop: 6 }}>
          {t('waitingTranslation')}
        </Txt>
      ) : null}
      {overLimit ? (
        <Txt size={13} color={colors.warningText} style={{ marginTop: 6 }}>
          {t('overLimit')}
        </Txt>
      ) : null}
      {englishWaiting ? (
        <Txt size={13} color={colors.textMuted} style={{ marginTop: 6 }}>
          {t('waitingTranslationEn')}
        </Txt>
      ) : null}
    </Card>
  );
}
